/**
 * Labels and small derivations shared by the support screens (blueprint 12 §8).
 *
 * CLIENT-SAFE: pure functions and constants only. The two case screens are client
 * components (they refresh, keyboard-navigate and post), so the "what does this
 * state mean on screen" answer must live somewhere that is not `server-only`.
 *
 * The vocabulary itself is not restated here: case types and their labels come
 * from `configs/support/playbooks.json` through the API, and the action list from
 * `lib/support/registry.ts`. This module only says how a stored value is drawn.
 */

import type { CaseState, Severity, SupportCase } from '@/lib/server/support/cases';

export type BadgeTone = 'danger' | 'warning' | 'success' | 'info' | 'secondary' | 'neutral';

export const SEVERITY_RANK: Record<Severity, number> = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3,
};

export const SEVERITY_TONE: Record<Severity, BadgeTone> = {
  critical: 'danger',
  high: 'warning',
  normal: 'info',
  low: 'secondary',
};

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

export const STATE_LABEL: Record<CaseState, string> = {
  new: 'New',
  triaged: 'Triaged',
  waiting_on_us: 'With us',
  waiting_on_them: 'Waiting on them',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const STATE_TONE: Record<CaseState, BadgeTone> = {
  new: 'info',
  triaged: 'secondary',
  waiting_on_us: 'warning',
  waiting_on_them: 'neutral',
  resolved: 'success',
  closed: 'secondary',
};

export const VIEW_LABEL = {
  queue: 'Queue',
  mine: 'Mine',
  escalated: 'Escalated',
  all: 'Everything',
  resolved: 'Resolved',
} as const;

export type SupportView = keyof typeof VIEW_LABEL;

/** Open states, mirrored from `OPEN_STATES` for the client's own filtering. */
export const OPEN_STATES: readonly CaseState[] = ['new', 'triaged', 'waiting_on_us', 'waiting_on_them'];

export interface SlaStatus {
  /** Which clock this is: the promise to answer, or the promise to finish. */
  kind: 'first_response' | 'resolve' | 'none';
  label: string;
  tone: BadgeTone;
  /** ISO deadline the label counts down/up from, when there is one. */
  deadline: string | null;
}

function minutesBetween(iso: string, now: number): number {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return 0;
  return Math.round((ms - now) / 60_000);
}

function window(minutes: number): string {
  const abs = Math.abs(minutes);
  if (abs < 60) return `${abs}m`;
  if (abs < 48 * 60) return `${Math.round(abs / 60)}h`;
  return `${Math.round(abs / (24 * 60))}d`;
}

/**
 * The one deadline that matters on this case right now.
 *
 * A case waiting on the requester has no clock running (blueprint §3.2): showing
 * "SLA breached" while we are the ones waiting would train everyone to ignore the
 * colour. A resolved case shows when it was resolved instead.
 */
export function slaStatus(supportCase: SupportCase, now = Date.now()): SlaStatus {
  if (supportCase.state === 'resolved' || supportCase.state === 'closed') {
    return { kind: 'none', label: 'SLA met (resolved)', tone: 'success', deadline: supportCase.resolvedAt };
  }
  if (supportCase.state === 'waiting_on_them') {
    return { kind: 'none', label: 'Clock paused', tone: 'neutral', deadline: null };
  }

  const firstResponseOpen = supportCase.firstResponseAt === null;
  const deadline = firstResponseOpen ? supportCase.slaFirstResponseAt : supportCase.slaResolveAt;
  const kind = firstResponseOpen ? 'first_response' : 'resolve';
  const remaining = minutesBetween(deadline, now);

  if (remaining < 0) {
    return {
      kind,
      label: `${firstResponseOpen ? 'Response' : 'Resolve'} ${window(remaining)} overdue`,
      tone: 'danger',
      deadline,
    };
  }
  if (remaining <= 15) {
    return {
      kind,
      label: `${firstResponseOpen ? 'Respond' : 'Resolve'} in ${window(remaining)}`,
      tone: 'warning',
      deadline,
    };
  }
  return {
    kind,
    label: `${firstResponseOpen ? 'Respond' : 'Resolve'} in ${window(remaining)}`,
    tone: 'secondary',
    deadline,
  };
}

/** "Unassigned", "Mine" or a short "Someone else has it" — never a raw uuid. */
export function assigneeLabel(supportCase: SupportCase, myUserId: string | null): string {
  if (!supportCase.assigneeId) return 'Unassigned';
  if (myUserId && supportCase.assigneeId === myUserId) return 'Mine';
  return 'Someone else';
}

export function isClaimedByOther(supportCase: SupportCase, myUserId: string | null): boolean {
  return Boolean(supportCase.assigneeId && myUserId && supportCase.assigneeId !== myUserId);
}

/** Human label for a stored case type, falling back to the slug. */
export function typeLabel(type: string, types: ReadonlyArray<{ type: string; label: string }>): string {
  return types.find((entry) => entry.type === type)?.label ?? type.replace(/_/g, ' ');
}

export function channelLabel(channel: string): string {
  switch (channel) {
    case 'in_app':
      return 'In-app';
    case 'email':
      return 'Email';
    case 'sms':
      return 'SMS';
    case 'push':
      return 'Push';
    default:
      return 'Not delivered';
  }
}

/** The subject line a trace link should point at, or null when there is none. */
export function traceTarget(supportCase: SupportCase): { kind: string; id: string } | null {
  if (!supportCase.subjectId) return null;
  if (supportCase.subjectKind === 'none') return null;
  return { kind: supportCase.subjectKind, id: supportCase.subjectId };
}
